import React from 'react';
import { Box, Container, Grid, Typography, useTheme, Chip } from '@mui/material';
import TopUtilityBar from '@/components/sections/TopUtilityBar';
import HeaderNav from '@/components/sections/HeaderNav';
import FooterSection from '@/components/sections/FooterSection';
import ClientLogosSection from '@/components/sections/ClientLogosSection';
import CorporateEvolutionSection from '@/components/sections/CorporateEvolutionSection';
import RFQFormSection from '@/components/sections/RFQFormSection';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';

import ManufacturingYardImg from '@/asset/manufacturing_yard.jpg';
import AboutHeroConstructionImg from '@/asset/about_hero_construction.jpg';
import PruthvikGuptaImg from '@/asset/pruthvik_gupta.jpg';
import SandeepHariwalImg from '@/asset/sandeep_hariwal.jpg';

export const AboutPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const milestones = [
    {
      year: '2015',
      title: 'Company Foundation',
      desc: 'Founded as a specialized civil construction and contracting firm in Bengaluru.',
    },
    {
      year: '2018',
      title: 'Façade & Infra Expansion',
      desc: 'Expanded into structural glazing, aluminium systems, architectural steel, and metro safety infrastructure.',
    },
    {
      year: '2024',
      title: 'Private Limited Incorporation',
      desc: 'Incorporated as Inverika Enterprises Private Limited (CIN: U41001KA2024PTC184411).',
    },
  ];

  const isoStandards = [
    {
      code: 'ISO 9001:2015',
      title: 'Quality Management System (QMS)',
      desc: 'Material verification, method statements, dimensional inspection, and structural load compliance.',
    },
    {
      code: 'ISO 14001:2015',
      title: 'Environmental Management System (EMS)',
      desc: 'Waste management, sustainable resource allocation, and eco-friendly site execution protocols.',
    },
    {
      code: 'ISO 45001:2018',
      title: 'Occupational Health & Safety (OHSMS)',
      desc: 'Zero-Harm safety culture, HIRA hazard risk assessment, and Permit-to-Work systems.',
    },
    {
      code: 'ISO 50001:2018',
      title: 'Energy Management System (EnMS)',
      desc: 'Energy-efficient fabrication processes and optimized off-site pre-assembly workflows.',
    },
  ];

  return (
    <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', color: theme.palette.text.primary, transition: 'background-color 0.3s ease, color 0.3s ease' }}>
      {/* Top Utility Bar with Dark/Light Theme Toggle Switch */}
      <TopUtilityBar />

      {/* Header Navigation */}
      <HeaderNav />

      {/* ========================================================= */}
      {/* 1. Hero Banner (About Us - Matching Design Screenshot)   */}
      {/* ========================================================= */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'relative',
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            {/* Left Text Column */}
            <Grid item xs={12} lg={7}>
              {/* Soft Blue Badge */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 1.5,
                  backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 80, 203, 0.08)',
                  border: `1px solid ${isDark ? 'rgba(0, 102, 255, 0.3)' : 'rgba(0, 80, 203, 0.2)'}`,
                  mb: 3,
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: theme.palette.primary.main }}>
                  article
                </span>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                  }}
                >
                  ABOUT INVERIKA
                </Typography>
              </Box>

              {/* Main Headline */}
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.25rem', sm: '3.25rem', md: '3.75rem' },
                  color: theme.palette.text.primary,
                  fontFamily: "'BankGothic Md BT', 'BankGothic MD BT', 'Bank Gothic MD BT', 'Space Grotesk', sans-serif",
                  fontWeight: 700,
                  lineHeight: 1.15,
                  letterSpacing: '0.01em',
                  textTransform: 'uppercase',
                  mb: 3,
                }}
              >
                ENGINEERED FOR EXCELLENCE.<br />
                <Box component="span" sx={{ color: theme.palette.primary.main }}>
                  BUILT ON DEPENDABLE<br />
                  EXECUTION.
                </Box>
              </Typography>

              {/* Subheadline Paragraph */}
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: { xs: '1rem', md: '1.05rem' },
                  lineHeight: 1.7,
                  mb: 4,
                  maxWidth: 580,
                }}
              >
                Founded in 2015 as a specialized contracting firm, Inverika Enterprises has evolved into a premier engineering and construction management powerhouse. We deliver mission-critical infrastructure for India's leading developers, adhering strictly to global technical standards and precision execution.
              </Typography>

              {/* Explore Our Legacy Button */}
              <Button
                variant="outlined"
                onClick={() => {
                  document.getElementById('leadership')?.scrollIntoView({ behavior: 'smooth' });
                }}
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.06em',
                  px: 3,
                  py: 1.25,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                EXPLORE OUR LEGACY
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                  arrow_forward
                </span>
              </Button>
            </Grid>

            {/* Right Column: High-Impact Construction Site Image Card */}
            <Grid item xs={12} lg={5}>
              <Box sx={{ position: 'relative', pt: { lg: 2 } }}>
                {/* Background Offset Card Shadow Effect */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: -16,
                    bottom: -16,
                    left: 16,
                    backgroundColor: isDark ? 'rgba(0, 102, 255, 0.1)' : 'rgba(0, 80, 203, 0.06)',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    zIndex: 0,
                    display: { xs: 'none', sm: 'block' },
                  }}
                />

                {/* Construction Site Card */}
                <Card
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    p: 0,
                    overflow: 'hidden',
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: isDark ? '0 12px 40px rgba(0,0,0,0.6)' : '0 12px 40px rgba(0,0,0,0.08)',
                    height: { xs: 340, sm: 440, md: 480 },
                  }}
                >
                  <Box
                    component="img"
                    src={AboutHeroConstructionImg}
                    alt="Project Skybridge Steel & Glazing Structure"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                    }}
                  />
                </Card>
              </Box>
            </Grid>
          </Grid>

          {/* Corporate Milestones Bar */}
          <Box sx={{ mt: 8, pt: 6, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Grid container spacing={4}>
              {milestones.map((item, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: 1,
                        backgroundColor: isDark ? 'rgba(0, 102, 255, 0.15)' : 'rgba(0, 80, 203, 0.1)',
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        flexShrink: 0,
                      }}
                    >
                      {item.year}
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.85rem', lineHeight: 1.5, mt: 0.25 }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* ========================================================= */}
      {/* 2. Trusted by Industry Leaders (Grey Client Logos)      */}
      {/* ========================================================= */}
      <ClientLogosSection />

      {/* ========================================================= */}
      {/* 2.5 Corporate Evolution Timeline                         */}
      {/* ========================================================= */}
      <CorporateEvolutionSection />

      {/* ========================================================= */}
      {/* 3. Executive Leadership Profiles (Matching Design Card Layout) */}
      {/* ========================================================= */}
      <Box id="leadership" sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                display: 'block',
                mb: 1,
              }}
            >
              OUR LEADERSHIP
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.75rem' },
                color: theme.palette.text.primary,
                fontFamily: 'Space Grotesk',
                fontWeight: 700,
                mb: 1.5,
              }}
            >
              Leadership and technical direction
            </Typography>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontSize: '1rem', maxWidth: 640 }}>
              Guided by seasoned engineers dedicated to structural integrity and operational excellence.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Profile 1: Pruthvik R. Gupta (Left Image, Right Content) */}
            <Card
              sx={{
                p: 0,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Grid container spacing={0}>
                <Grid item xs={12} md={5} lg={4}>
                  <Box
                    component="img"
                    src={PruthvikGuptaImg}
                    alt="Pruthvik R. Gupta"
                    sx={{
                      width: '100%',
                      height: { xs: 320, md: '100%' },
                      minHeight: { md: 380 },
                      objectFit: 'cover',
                      objectPosition: 'center 20%',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={7} lg={8}>
                  <Box sx={{ p: { xs: 4, md: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      FOUNDER & MANAGING DIRECTOR
                    </Typography>

                    <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700, fontFamily: 'Space Grotesk', mb: 2 }}>
                      Pruthvik R. Gupta
                    </Typography>

                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, mb: 3, maxWidth: 680 }}>
                      10+ Years in civil construction, façade engineering, glazing, turnkey contracting, estimation, procurement, and execution. Spearheaded Inverika's growth from a civil contractor into an integrated engineering partner. Leads strategic direction.
                    </Typography>

                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 1.5,
                        backgroundColor: isDark ? 'rgba(0, 102, 255, 0.08)' : 'rgba(0, 80, 203, 0.04)',
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.primary, lineHeight: 1.6 }}>
                        "Our commitment is not simply to complete a scope of work, but to redefine infrastructure through precision engineering, zero-harm safety, and unyielding execution discipline."
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Card>

            {/* Profile 2: Sandeep Hariwal (Left Content, Right Image) */}
            <Card
              sx={{
                p: 0,
                overflow: 'hidden',
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Grid container spacing={0} direction={{ xs: 'column-reverse', md: 'row' }}>
                <Grid item xs={12} md={7} lg={8}>
                  <Box sx={{ p: { xs: 4, md: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        display: 'block',
                        mb: 1,
                      }}
                    >
                      SR. GENERAL MANAGER - CONSTRUCTION
                    </Typography>

                    <Typography variant="h4" sx={{ color: theme.palette.text.primary, fontWeight: 700, fontFamily: 'Space Grotesk', mb: 2 }}>
                      Sandeep Hariwal
                    </Typography>

                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, mb: 4, maxWidth: 680 }}>
                      14+ Years in civil construction, interior fit-outs, and construction management across hospitality, commercial, and residential sectors. Led major execution roles for Four Points by Sheraton (Cochin) and Brigade Tech Gardens (Bengaluru). Heads Inverika's construction division, managing site planning, contract administration, resource optimization, and quality control across all active sites.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                      <Chip label="EXECUTION STRATEGY" size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                      <Chip label="SITE LOGISTICS" size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                      <Chip label="QUALITY CONTROL" size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={5} lg={4}>
                  <Box
                    component="img"
                    src={SandeepHariwalImg}
                    alt="Sandeep Hariwal"
                    sx={{
                      width: '100%',
                      height: { xs: 320, md: '100%' },
                      minHeight: { md: 380 },
                      objectFit: 'cover',
                      objectPosition: 'center 20%',
                    }}
                  />
                </Grid>
              </Grid>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* ========================================================= */}
      {/* 4. In-House Manufacturing Yard (with Facility Image)      */}
      {/* ========================================================= */}
      <Box sx={{ py: { xs: 10, md: 14 }, backgroundColor: isDark ? 'rgba(22, 29, 47, 0.4)' : '#f1f5f9', borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
                MANUFACTURING EXCELLENCE
              </Typography>
              <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, color: theme.palette.text.primary, fontFamily: 'Space Grotesk', fontWeight: 700, mb: 3 }}>
                7,000 sq.ft. In-House Manufacturing & Pre-Assembly Yard
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7, mb: 4 }}>
                Located at Sarjapur Road, Bengaluru, our dedicated pre-assembly facility eliminates site delays and enforces stringent dimensional tolerances before on-site deployment.
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6}>
                  <Card sx={{ p: 3, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                    <Typography variant="h4" sx={{ color: theme.palette.primary.main, fontFamily: 'Space Grotesk', fontWeight: 700 }}>
                      50 MT
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Structural Steel / Month
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card sx={{ p: 3, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                    <Typography variant="h4" sx={{ color: theme.palette.primary.main, fontFamily: 'Space Grotesk', fontWeight: 700 }}>
                      20,000 sq.ft.
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Aluminium Façade / Month
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              <Card sx={{ p: 3 }}>
                <Typography variant="subtitle2" sx={{ fontFamily: 'Space Grotesk', fontWeight: 700, mb: 2 }}>
                  Machinery Fleet & Precision Tools
                </Typography>
                <Grid container spacing={2}>
                  {[
                    'CNC Double-Head Cutting Machines',
                    'CNC Copy Routers & End Milling',
                    'Corner Crimping Machines',
                    'Industrial Punching Press Fleet',
                    'MIG / TIG Welding Stations',
                    'CNC Engraving & Laser Tools',
                  ].map((tool, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: theme.palette.primary.main }}>
                          build_circle
                        </span>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500, fontSize: '0.85rem' }}>
                          {tool}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>

            {/* Manufacturing Yard Facility Image */}
            <Grid item xs={12} lg={6}>
              <Card sx={{ p: 1, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', height: { xs: 320, md: 520 } }}>
                <Box
                  component="img"
                  src={ManufacturingYardImg}
                  alt="Inverika In-House Manufacturing Yard Facility"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ========================================================= */}
      {/* 5. ISO Certifications & Zero-Harm Safety Culture           */}
      {/* ========================================================= */}
      <Box sx={{ py: { xs: 10, md: 14 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', mb: 1 }}>
              INSTITUTIONAL QUALITY & SAFETY
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, color: theme.palette.text.primary, fontFamily: 'Space Grotesk', fontWeight: 700 }}>
              Integrated Management System (ISO Certified)
            </Typography>
          </Box>

          <Grid container spacing={4} sx={{ mb: 8 }}>
            {isoStandards.map((std, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card sx={{ p: 3, height: '100%', border: `1px solid ${theme.palette.divider}` }}>
                  <Typography variant="subtitle2" sx={{ color: theme.palette.primary.main, fontWeight: 700, letterSpacing: '0.05em', mb: 1 }}>
                    {std.code}
                  </Typography>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, mb: 1.5, color: theme.palette.text.primary }}>
                    {std.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.875rem', lineHeight: 1.6 }}>
                    {std.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Zero-Harm Safety Box */}
          <Card sx={{ p: 4, backgroundColor: isDark ? 'rgba(0, 102, 255, 0.08)' : 'rgba(0, 80, 203, 0.05)', border: `1px solid ${theme.palette.primary.main}` }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="h4" sx={{ color: theme.palette.primary.main, fontFamily: 'Space Grotesk', fontWeight: 700, mb: 1 }}>
                  Zero-Harm Safety Culture
                </Typography>

                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                  Strict safety enforcement protecting site manpower and high-value project assets during at-height glazing and structural erection.
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  {[
                    'Daily Toolbox Talks (TBT) & Site Inductions',
                    'Hazard Risk Assessment (HIRA) Protocols',
                    'Permit-to-Work (PTW) for High-Risk Glazing',
                    'Compulsory 100% PPE & Mock Emergency Drills',
                  ].map((safetyItem, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 20, color: theme.palette.primary.main }}>
                          verified_user
                        </span>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                          {safetyItem}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {/* ========================================================= */}
      {/* 6. Discuss Your Project / RFQ Form Section               */}
      {/* ========================================================= */}
      <RFQFormSection />

      {/* Footer */}
      <FooterSection />
    </Box>
  );
};

export default AboutPage;
